import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTools, 
  faCheckCircle, 
  faExclamationTriangle, 
  faSpinner,
  faRefresh,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import { woocommerceService } from '../../../services/woocommerceService';

const DebugContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  color: #333;
`;

const TestSection = styled.div`
  margin-bottom: 2rem;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

const TestTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #333;
`;

const TestResult = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  &.success {
    color: #28a745;
  }
  
  &.error {
    color: #dc3545;
  }
  
  &.loading {
    color: #6c757d;
  }
`;

const CodeBlock = styled.pre`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.875rem;
  border: 1px solid #e9ecef;
  max-height: 300px;
  white-space: pre-wrap;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #007cba;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 0.5rem;
  
  &:hover {
    background: #005a87;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'success':
      return <FontAwesomeIcon icon={faCheckCircle} className="success" />;
    case 'error':
      return <FontAwesomeIcon icon={faExclamationTriangle} className="error" />;
    case 'loading':
      return <FontAwesomeIcon icon={faSpinner} spin className="loading" />;
    default:
      return null;
  }
};

const WooCommerceDebug = () => {
  const [tests, setTests] = useState({
    connection: { status: 'idle', result: null },
    cart: { status: 'idle', result: null },
    paymentMethods: { status: 'idle', result: null },
    storeApi: { status: 'idle', result: null }
  });

  const updateTest = (testName, status, result = null) => {
    setTests(prev => ({
      ...prev,
      [testName]: { status, result }
    }));
  };

  const runTest = async (testName, testFn) => {
    updateTest(testName, 'loading');
    try {
      const result = await testFn();
      updateTest(testName, 'success', result);
    } catch (error) {
      updateTest(testName, 'error', error.message);
    }
  };

  const testConnection = async () => {
    return await woocommerceService.testConnection();
  };

  const testCart = async () => {
    return await woocommerceService.getCart();
  };

  const testPaymentMethods = async () => {
    return await woocommerceService.getPaymentMethods();
  };

  const testStoreApi = async () => {
    return await woocommerceService.debugStoreApi();
  };

  const runAllTests = async () => {
    await runTest('connection', testConnection);
    await runTest('cart', testCart);
    await runTest('paymentMethods', testPaymentMethods);
    await runTest('storeApi', testStoreApi);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatResult = (result) => {
    if (typeof result === 'string') return result;
    return JSON.stringify(result, null, 2);
  };

  useEffect(() => {
    // Auto-run tests on component mount
    runAllTests();
  }, []);

  return (
    <DebugContainer>
      <Title>
        <FontAwesomeIcon icon={faTools} />
        WooCommerce Integration Debug
      </Title>

      <Button onClick={runAllTests}>
        <FontAwesomeIcon icon={faRefresh} />
        Run All Tests
      </Button>

      <TestSection>
        <TestTitle>
          <StatusIcon status={tests.connection.status} />
          API Connection Test
        </TestTitle>
        <TestResult className={tests.connection.status}>
          {tests.connection.status === 'loading' && 'Testing API connection...'}
          {tests.connection.status === 'success' && 'API connection successful'}
          {tests.connection.status === 'error' && `API connection failed: ${tests.connection.result}`}
        </TestResult>
        <Button onClick={() => runTest('connection', testConnection)} disabled={tests.connection.status === 'loading'}>
          Test Connection
        </Button>
        {tests.connection.result && (
          <>
            <Button onClick={() => copyToClipboard(formatResult(tests.connection.result))}>
              <FontAwesomeIcon icon={faCopy} />
              Copy Result
            </Button>
            <CodeBlock>{formatResult(tests.connection.result)}</CodeBlock>
          </>
        )}
      </TestSection>

      <TestSection>
        <TestTitle>
          <StatusIcon status={tests.cart.status} />
          Store API Cart Test
        </TestTitle>
        <TestResult className={tests.cart.status}>
          {tests.cart.status === 'loading' && 'Testing cart API...'}
          {tests.cart.status === 'success' && 'Cart API working correctly'}
          {tests.cart.status === 'error' && `Cart API failed: ${tests.cart.result}`}
        </TestResult>
        <Button onClick={() => runTest('cart', testCart)} disabled={tests.cart.status === 'loading'}>
          Test Cart API
        </Button>
        {tests.cart.result && (
          <>
            <Button onClick={() => copyToClipboard(formatResult(tests.cart.result))}>
              <FontAwesomeIcon icon={faCopy} />
              Copy Result
            </Button>
            <CodeBlock>{formatResult(tests.cart.result)}</CodeBlock>
          </>
        )}
      </TestSection>

      <TestSection>
        <TestTitle>
          <StatusIcon status={tests.paymentMethods.status} />
          Payment Methods Test
        </TestTitle>
        <TestResult className={tests.paymentMethods.status}>
          {tests.paymentMethods.status === 'loading' && 'Fetching available payment methods...'}
          {tests.paymentMethods.status === 'success' && 'Payment methods loaded successfully'}
          {tests.paymentMethods.status === 'error' && `Payment methods failed: ${tests.paymentMethods.result}`}
        </TestResult>
        <Button onClick={() => runTest('paymentMethods', testPaymentMethods)} disabled={tests.paymentMethods.status === 'loading'}>
          Test Payment Methods
        </Button>
        {tests.paymentMethods.result && (
          <>
            <Button onClick={() => copyToClipboard(formatResult(tests.paymentMethods.result))}>
              <FontAwesomeIcon icon={faCopy} />
              Copy Result
            </Button>
            <CodeBlock>{formatResult(tests.paymentMethods.result)}</CodeBlock>
          </>
        )}
      </TestSection>

      <TestSection>
        <TestTitle>
          <StatusIcon status={tests.storeApi.status} />
          Store API Debug
        </TestTitle>
        <TestResult className={tests.storeApi.status}>
          {tests.storeApi.status === 'loading' && 'Running Store API diagnostics...'}
          {tests.storeApi.status === 'success' && 'Store API diagnostics completed'}
          {tests.storeApi.status === 'error' && `Store API diagnostics failed: ${tests.storeApi.result}`}
        </TestResult>
        <Button onClick={() => runTest('storeApi', testStoreApi)} disabled={tests.storeApi.status === 'loading'}>
          Run Store API Debug
        </Button>
        {tests.storeApi.result && (
          <>
            <Button onClick={() => copyToClipboard(formatResult(tests.storeApi.result))}>
              <FontAwesomeIcon icon={faCopy} />
              Copy Result
            </Button>
            <CodeBlock>{formatResult(tests.storeApi.result)}</CodeBlock>
          </>
        )}
      </TestSection>
    </DebugContainer>
  );
};

export default WooCommerceDebug;